import { WebSocketHelper } from 'classes/helpers/web-socket.helper';
import { MachinesPortalState } from 'components/sections/admin-portal/machines/store/use-machines-portal-store';
import env from 'config';
import { MachinesView } from 'enums/admin-portal';
import { WsMsgType } from 'lib_ts/enums/machine-msg.enum';
import { MachineStatus } from 'lib_ts/enums/machine.enums';
import { DictOf } from 'lib_ts/interfaces/common/i-dictionary';
import { IAdminMachine, IMachine } from 'lib_ts/interfaces/i-machine';
import { DEFAULT_MACHINE_SUMMARY } from 'lib_ts/interfaces/i-machine-summary';
import { IMachinesSummaryResponseMsg } from 'lib_ts/interfaces/machine-msg/i-machines-summary';
import { IMachineModel } from 'lib_ts/interfaces/modelling/i-machine-model';
import { ITrainingLogSummary } from 'lib_ts/interfaces/training/i-training-log';
import { clone, isEqual, merge } from 'lodash';
import { AdminMachinesService } from 'services/admin/machines.service';
import { WebSocketService } from 'services/web-socket.service';
import { StateCreator } from 'zustand';

const STORE_NAME = 'MachinesPortalStore.MachinesSlice';
const MACHINES_SUMMARY_TIMEOUT_MS = 10000; // responses are generally sent out every 5 seconds

interface IMachinesFilters {
  machineIDs: string[];
  teams: string[];
  created: string[];
  firmware: string[];
  statuses: MachineStatus[];
  isDeleted: boolean;
  isConnected?: boolean;
  hasActive?: boolean;
}

interface MachinesSliceState {
  // more granular loading state
  loading: boolean;
  updating: boolean;
  viewMode: MachinesView;
  tpSummaryDict: DictOf<ITrainingLogSummary>;

  // machines as shown in the table
  machines: IAdminMachine[];
  models: IMachineModel[];

  // machines table filters
  filters: IMachinesFilters;

  // whether to refetch machines when the dialog is closed (e.g. after updating an important field)
  shouldRefetchMachines?: boolean;

  // subscription state
  subscribing: boolean; // true when we've made a summary request but haven't received a response yet
  subscribed: boolean; // keeps track if we're still receiving messages
  subscriptionLastUpdated?: string;
}

type MachinesSliceActions = {
  setLoading: (loading: boolean) => void;
  setViewMode: (mode: MachinesView) => void;
  setTpSummaryDict: (value: DictOf<ITrainingLogSummary>) => void;

  startSubscription: () => Promise<void>;
  stopSubscription: () => Promise<void>;
  restartSubscription: () => Promise<void>;

  fetchMachinesAndModels: () => Promise<void>;
  setShouldRefetchMachines: (shouldRefetchMachines: boolean) => void;

  mergeMachineFilters: (filters: Partial<IMachinesFilters>) => void;

  createMachine: (
    machine: Partial<IMachine>
  ) => Promise<IAdminMachine | undefined>;

  updateMachines: (
    payloads: Partial<IMachine>[],
    silently?: boolean
  ) => Promise<IAdminMachine[] | undefined>;
};

export type MachinesSlice = MachinesSliceState & MachinesSliceActions;

type CreateMachinesSlice = StateCreator<
  MachinesPortalState,
  [['zustand/devtools', never]],
  [],
  MachinesSlice
>;

const initialState: MachinesSliceState = {
  loading: false,
  updating: false,
  viewMode: MachinesView.DefaultView,
  tpSummaryDict: {},

  machines: [],
  models: [],

  filters: {
    machineIDs: [],
    teams: [],
    created: [],
    firmware: [],
    statuses: [],
    isConnected: env.production ? true : undefined,
    isDeleted: false,
  },

  subscribing: false,
  subscribed: false,
};

export const createMachinesSlice: CreateMachinesSlice = (set, get) => {
  // private helpers, not available outside this scope
  const onMachinesSummaryMsg = (event: CustomEvent) => {
    const data: IMachinesSummaryResponseMsg = event.detail;
    const { machines: summaries, timestamp } = data;

    set((state) => {
      const baseUpdates = {
        subscribing: false,
        subscribed: true,
        subscriptionLastUpdated: timestamp,
      };

      let changed = false;

      const newMachines = state.machines.map((machine) => {
        const summary = summaries.find(
          (u) => u.machineID === machine.machineID
        );

        const merged: IAdminMachine = summary
          ? {
              ...machine,
              // need to explicitly set these instead of spreading since JSON doesn't provide keys for undefined fields
              machineID: summary.machineID,
              connected: summary.connected,
              active: summary.active,
              machine_status: summary.machine_status,
              ball_type: summary.ball_type,
            }
          : { ...machine, connected: false }; // machine is not connected, no live updates to share

        if (!isEqual(machine, merged)) {
          changed = true;
          return merged;
        }

        return machine;
      });

      // no updates
      if (!changed) {
        return baseUpdates;
      }

      return {
        ...baseUpdates,
        machines: newMachines,
      };
    });

    startSummaryTimer();
  };

  let summarySubTimer: NodeJS.Timeout | null = null;

  const stopSummaryTimer = () => {
    if (summarySubTimer) {
      clearTimeout(summarySubTimer);
      summarySubTimer = null;
    }
  };

  // if we haven't received a msg within the timeout, flag as disconnected
  const startSummaryTimer = () => {
    stopSummaryTimer();

    summarySubTimer = setTimeout(() => {
      set({ subscribed: false });
    }, MACHINES_SUMMARY_TIMEOUT_MS);
  };

  return {
    ...initialState,

    setLoading: (loading) => {
      set({
        loading,
      });
    },

    setViewMode: (mode) => {
      set({
        viewMode: mode,
      });
    },

    setTpSummaryDict: (value) => {
      set({
        tpSummaryDict: value,
      });
    },

    fetchMachinesAndModels: async () => {
      set({
        loading: true,
      });

      const machines =
        await AdminMachinesService.getInstance().getMachines(true);
      const models = await AdminMachinesService.getInstance().getActiveModels();

      set({
        loading: false,
        machines,
        models,
      });
    },

    startSubscription: async () => {
      WebSocketService.send(
        WsMsgType.U2S_MachinesSummary,
        { enable: true },
        STORE_NAME
      );

      set({
        subscribing: true,
      });

      WebSocketHelper.on(WsMsgType.S2U_MachinesSummary, onMachinesSummaryMsg);

      startSummaryTimer();
    },

    stopSubscription: async () => {
      WebSocketService.send(
        WsMsgType.U2S_MachinesSummary,
        { enable: false },
        STORE_NAME
      );

      WebSocketHelper.remove(
        WsMsgType.S2U_MachinesSummary,
        onMachinesSummaryMsg
      );

      stopSummaryTimer();

      set({
        subscribing: false,
        subscribed: false,
        subscriptionLastUpdated: undefined,
      });
    },

    restartSubscription: async () => {
      set({ loading: true });

      const { startSubscription, stopSubscription } = get();

      const machines =
        await AdminMachinesService.getInstance().getMachines(true);

      stopSubscription();
      startSubscription();

      set({
        machines: machines,
        loading: false,
      });
    },

    setShouldRefetchMachines: (shouldRefetchMachines) => {
      set({
        shouldRefetchMachines,
      });
    },

    mergeMachineFilters: (filters) => {
      set((state) => ({
        filters: {
          ...state.filters,
          ...filters,
        },
      }));
    },

    createMachine: async (machine) => {
      try {
        set({
          updating: true,
        });

        const result = await AdminMachinesService.getInstance().create(machine);

        if (!result) {
          return;
        }

        //append to end of list
        set((state) => ({
          machines: [...state.machines, result],
        }));

        return result;
      } catch (error) {
        console.error(error);
        throw error; // rethrow to handle in the calling function
      } finally {
        set({
          updating: false,
        });
      }
    },

    // mostly to handle loading state gracefully when updating multiple machines
    updateMachines: async (payloads, silently) => {
      if (!silently) {
        set({
          updating: true,
        });
      }

      try {
        const service = AdminMachinesService.getInstance();

        const results = await Promise.all(
          payloads.map((m) => service.updateMachine(m))
        );

        if (results.length === 0) {
          // skip updating state if it failed
          return [];
        }

        set((state) => {
          const { machine: editorMachine, machines: tableMachines } = state;

          // update the machines as shown in the MachinesTable
          const nextMachines = [...tableMachines];

          results.forEach((result) => {
            if (!result) {
              // shouldn't trigger, but skip any failed updates
              return;
            }

            const existing = nextMachines.find((v) => v._id === result._id);

            // append to end of list
            if (!existing) {
              // add some default values
              merge(result, DEFAULT_MACHINE_SUMMARY);

              // try to find game status from another machine belonging to the same team
              result.isInGame = result.ignore_game_status
                ? false
                : (nextMachines.find((m) => m._parent_id === result._parent_id)
                    ?.isInGame ?? false);

              nextMachines.push(result);
              return;
            }

            // overwrite existing
            merge(existing, result);
          });

          // update the machine as shown in the MachineSettingsDialog (if needed)
          const nextMachine = clone(editorMachine);

          const updatedMachine = nextMachines.find(
            (m) => m?._id === editorMachine?._id
          );

          merge(nextMachine, updatedMachine);

          return {
            machines: nextMachines,
            machine: nextMachine,
          };
        });

        const { machines } = get();

        const output = payloads
          .map((p) => machines.find((m) => m._id === p._id))
          .filter((m) => !!m) as IAdminMachine[];

        return output;
      } catch (error) {
        console.error(error);
        throw error; // rethrow to handle in the calling function
      } finally {
        if (!silently) {
          set({
            updating: false,
          });
        }
      }
    },
  };
};
